/**
 * Deterministic wheel math. The server has already chosen and persisted the winner; this only computes an
 * animation that is guaranteed to stop with the pointer (top, 12 o'clock) inside the winner's segment.
 *
 * Wheel frame: segment i spans [i*seg, (i+1)*seg) degrees measured clockwise from the top.
 * A clockwise rotation R puts wheel angle (360 - R mod 360) under the pointer.
 */
export const FULL_TURNS = 6;

export function segmentAngle(count: number): number {
  if (count < 1) throw new Error('Wheel needs at least one segment');
  return 360 / count;
}

/** Final absolute rotation (degrees, clockwise) that lands the pointer on winnerIndex. jitter in [-0.5, 0.5]. */
export function finalRotation(count: number, winnerIndex: number, jitter = 0, turns = FULL_TURNS): number {
  if (winnerIndex < 0 || winnerIndex >= count) throw new Error('Winner index out of range');
  const seg = segmentAngle(count);
  const j = Math.max(-0.45, Math.min(0.45, jitter));
  const target = (winnerIndex + 0.5 + j) * seg; // wheel-frame angle that must end under the pointer
  return turns * 360 + ((360 - target) % 360 + 360) % 360;
}

/** Ease-out quartic: fast start, long dramatic slow-down, exact stop at t=1. */
export function easeOutQuart(t: number): number {
  const x = Math.max(0, Math.min(1, t));
  return 1 - Math.pow(1 - x, 4);
}

/** Rotation at a given elapsed time. Elapsed may be negative (not started) or beyond the duration (finished). */
export function rotationAt(elapsedMs: number, spinSeconds: number, final: number): number {
  if (spinSeconds <= 0) return final;
  return final * easeOutQuart(elapsedMs / (spinSeconds * 1000));
}

/** Index of the segment currently under the pointer for a rotation. */
export function indexUnderPointer(count: number, rotation: number): number {
  const seg = segmentAngle(count);
  const a = ((360 - (rotation % 360)) % 360 + 360) % 360;
  return Math.min(count - 1, Math.floor(a / seg));
}

/**
 * Milliseconds elapsed since the server-recorded start, corrected for client clock skew using the server's
 * clock at the moment the payload was produced. Makes a display that reconnects mid-spin resume exactly.
 */
export function elapsedSinceStart(startedAtUtc: string, serverNowUtc: string, receivedAtClientMs: number, clientNowMs = Date.now()): number {
  const serverElapsedAtReceive = Date.parse(serverNowUtc) - Date.parse(startedAtUtc);
  return serverElapsedAtReceive + (clientNowMs - receivedAtClientMs);
}

export const SEGMENT_COLORS = ['#123B5D', '#2F80C8', '#1F5F8F', '#5AA8DA'] as const;
/** Text color for a segment fill (navy on the light blue for contrast, white otherwise). */
export function segmentTextColor(fill: string): string { return fill === '#5AA8DA' ? '#0B2540' : '#FFFFFF'; }

export function segmentColor(i: number, count: number): string {
  // Avoid identical neighbours when the count wraps around the palette.
  const palette = SEGMENT_COLORS;
  let c = palette[i % palette.length]!;
  if (i === count - 1 && count % palette.length === 1 && count > 1) c = palette[2]!;
  return c;
}

/** Labels are drawn when they fit; otherwise the display shows a live "pointer name" readout instead. */
export const MAX_LABELLED_SEGMENTS = 36;
